import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { Link } from 'react-router-dom'

type Tab = 'community' | 'profile' | 'users'

interface CommunityUser {
  id: string
  name: string
  display_name?: string
  email?: string
  role: string
  is_human: number
  is_persona: number
  visibility?: string
  home_private?: number
  status: string
  last_seen_at?: string
  created_at: string
  password_hash?: undefined // stripped by server
}

interface CommunityData {
  id: string
  name: string
  display_name?: string
  slug: string
  settings?: string
}

function getToken() {
  return (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1]) || ''
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }
}

// ═══ Community Settings Tab ═══
function CommunitySettingsTab() {
  const [communityData, setCommunityData] = useState<CommunityData | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [accentColor, setAccentColor] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/settings/community', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setCommunityData(data)
        setDisplayName(data.display_name || data.name || '')
        const settings = data.settings ? (typeof data.settings === 'string' ? JSON.parse(data.settings) : data.settings) : {}
        setAccentColor(settings.accent_color || '')
        setLogoUrl(settings.logo_url || '')
      })
      .catch(() => setMessage('Failed to load community settings'))
  }, [])

  async function save() {
    if (!communityData) return
    setSaving(true)
    setMessage(null)
    try {
      const currentSettings = communityData.settings ? (typeof communityData.settings === 'string' ? JSON.parse(communityData.settings) : communityData.settings) : {}
      const newSettings = { ...currentSettings }
      if (accentColor) newSettings.accent_color = accentColor
      else delete newSettings.accent_color
      if (logoUrl) newSettings.logo_url = logoUrl
      else delete newSettings.logo_url

      const res = await fetch('/api/settings/community', {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ display_name: displayName, settings: newSettings }),
      })
      if (res.ok) setMessage('Saved')
      else setMessage('Failed to save')
    } catch { setMessage('Error saving') }
    setSaving(false)
  }

  if (!communityData) return <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</p>

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Community Display Name</label>
        <input
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          className="w-full px-3 py-2 rounded text-sm outline-none"
          style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
        />
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Accent Color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={accentColor || '#1B4965'}
            onChange={e => setAccentColor(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer border-0"
          />
          <input
            value={accentColor}
            onChange={e => setAccentColor(e.target.value)}
            placeholder="#1B4965"
            className="flex-1 px-3 py-2 rounded text-sm outline-none font-mono"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Logo URL</label>
        <input
          value={logoUrl}
          onChange={e => setLogoUrl(e.target.value)}
          placeholder="https://..."
          className="w-full px-3 py-2 rounded text-sm outline-none"
          style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
        />
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="text-xs px-4 py-2 rounded-md disabled:opacity-50"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        {message && <span className="text-xs" style={{ color: message === 'Saved' ? 'var(--success, #4ade80)' : 'var(--danger)' }}>{message}</span>}
      </div>
    </div>
  )
}

// ═══ My Profile Tab ═══
function MyProfileTab() {
  const { user } = useAuth()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [homePrivate, setHomePrivate] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [profileMsg, setProfileMsg] = useState<string | null>(null)
  const [pwMsg, setPwMsg] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetch('/api/settings/profile', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setDisplayName(data.display_name || data.name || '')
        setEmail(data.email || '')
        setHomePrivate(!!data.home_private)
      })
  }, [])

  async function saveProfile() {
    setSaving(true)
    setProfileMsg(null)
    try {
      const res = await fetch('/api/settings/profile', {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ display_name: displayName, email, home_private: homePrivate }),
      })
      setProfileMsg(res.ok ? 'Saved' : 'Failed')
    } catch { setProfileMsg('Error') }
    setSaving(false)
  }

  async function changePassword() {
    setPwMsg(null)
    if (newPassword.length < 6) { setPwMsg('Minimum 6 characters'); return }
    if (newPassword !== confirmPassword) { setPwMsg('Passwords do not match'); return }
    try {
      const res = await fetch('/api/settings/password', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      })
      if (res.ok) {
        setPwMsg('Password updated')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      } else {
        const data = await res.json()
        setPwMsg(data.error || 'Failed')
      }
    } catch { setPwMsg('Error') }
  }

  return (
    <div className="space-y-6">
      {/* Profile info */}
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Display Name</label>
          <input
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 rounded text-sm outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Email</label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full px-3 py-2 rounded text-sm outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="home-private"
            checked={homePrivate}
            onChange={e => setHomePrivate(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="home-private" className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Make my Home private (hidden from Neighborhood)
          </label>
        </div>
        <div>
          <Link
            to={`/neighborhood/${user?.loginName || ''}`}
            className="text-xs no-underline"
            style={{ color: 'var(--accent)' }}
          >
            Edit my Home →
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={saveProfile}
            disabled={saving}
            className="text-xs px-4 py-2 rounded-md disabled:opacity-50"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {profileMsg && <span className="text-xs" style={{ color: profileMsg === 'Saved' ? 'var(--success, #4ade80)' : 'var(--danger)' }}>{profileMsg}</span>}
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--border)' }} />

      {/* Change password */}
      <div>
        <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>Change Password</h3>
        <div className="space-y-2 max-w-sm">
          <input
            type="password"
            placeholder="Current password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            className="w-full px-3 py-2 rounded text-sm outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            className="w-full px-3 py-2 rounded text-sm outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-2 rounded text-sm outline-none"
            style={{ background: 'var(--bg-body)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={changePassword}
              disabled={!newPassword}
              className="text-xs px-4 py-2 rounded-md disabled:opacity-50"
              style={{ background: 'var(--accent)', color: '#fff' }}
            >
              Update Password
            </button>
            {pwMsg && <span className="text-xs" style={{ color: pwMsg === 'Password updated' ? 'var(--success, #4ade80)' : 'var(--danger)' }}>{pwMsg}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══ Users Management Tab ═══
function UsersTab() {
  const { user } = useAuth()
  const [users, setUsers] = useState<CommunityUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  // Add form state
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newType, setNewType] = useState<'person' | 'persona'>('person')
  const [newRole, setNewRole] = useState<'admin' | 'member'>('member')
  const [newVisibility, setNewVisibility] = useState<'private' | 'public'>('private')
  const [addMsg, setAddMsg] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  async function loadUsers() {
    try {
      const res = await fetch('/api/settings/users', { headers: authHeaders() })
      if (res.ok) setUsers(await res.json())
    } catch { /* ignore */ }
    setLoading(false)
  }

  useEffect(() => { loadUsers() }, [])

  async function addUser() {
    if (!newName.trim()) { setAddMsg('Name is required'); return }
    if (!newEmail.trim()) { setAddMsg('Email is required'); return }
    setAdding(true)
    setAddMsg(null)
    try {
      const res = await fetch(`/api/communities/${user?.communityId}/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: newName.trim(),
          email: newEmail.trim(),
          role: newRole,
          is_human: newType === 'person' ? 1 : 0,
          is_persona: newType === 'persona' ? 1 : 0,
          visibility: newType === 'persona' ? newVisibility : 'private',
        }),
      })
      if (res.ok) {
        setAddMsg('User created — invite email sent')
        setNewName('')
        setNewEmail('')
        setNewType('person')
        setNewRole('member')
        setNewVisibility('private')
        setShowAddForm(false)
        loadUsers()
      } else {
        const data = await res.json()
        setAddMsg(data.error || 'Failed to create user')
      }
    } catch { setAddMsg('Error creating user') }
    setAdding(false)
  }

  async function updateUser(userId: string, updates: Record<string, unknown>) {
    try {
      const res = await fetch(`/api/communities/${user?.communityId}/users/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(updates),
      })
      if (res.ok) {
        loadUsers()
        setEditingId(null)
      }
    } catch { /* ignore */ }
  }

  async function resendInvite(userId: string) {
    try {
      const res = await fetch('/api/settings/users/resend-invite', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ user_id: userId }),
      })
      if (res.ok) alert('Invite email sent')
      else {
        const data = await res.json()
        alert(data.error || 'Failed to resend')
      }
    } catch { alert('Error') }
  }

  if (loading) return <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</p>

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{users.length} user{users.length !== 1 ? 's' : ''}</span>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {showAddForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {/* Add user form */}
      {showAddForm && (
        <div className="p-4 rounded-lg mb-4 space-y-3" style={{ background: 'var(--bg-body)', border: '1px solid var(--border)' }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Name *</label>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Full name"
                className="w-full px-3 py-2 rounded text-sm outline-none"
                style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Email *</label>
              <input
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="email@example.com"
                type="email"
                className="w-full px-3 py-2 rounded text-sm outline-none"
                style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Type</label>
              <select
                value={newType}
                onChange={e => setNewType(e.target.value as 'person' | 'persona')}
                className="w-full px-3 py-2 rounded text-sm outline-none"
                style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              >
                <option value="person">Person</option>
                <option value="persona">Persona</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Role</label>
              <select
                value={newRole}
                onChange={e => setNewRole(e.target.value as 'admin' | 'member')}
                className="w-full px-3 py-2 rounded text-sm outline-none"
                style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          {newType === 'persona' && (
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Visibility</label>
              <select
                value={newVisibility}
                onChange={e => setNewVisibility(e.target.value as 'private' | 'public')}
                className="w-full px-3 py-2 rounded text-sm outline-none"
                style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              >
                <option value="private">Private — Community members only</option>
                <option value="public">Public — Visible to The Gathering</option>
              </select>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={addUser}
              disabled={adding || !newName.trim() || !newEmail.trim()}
              className="text-xs px-4 py-2 rounded-md disabled:opacity-50"
              style={{ background: 'var(--accent)', color: '#fff' }}
            >
              {adding ? 'Creating...' : 'Create User & Send Invite'}
            </button>
            {addMsg && <span className="text-xs" style={{ color: addMsg.includes('created') ? 'var(--success, #4ade80)' : 'var(--danger)' }}>{addMsg}</span>}
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: 'var(--bg-body)' }}>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Name</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Email</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Type</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Role</th>
              <th className="text-left px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
              <th className="text-right px-3 py-2 font-medium" style={{ color: 'var(--text-muted)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr
                key={u.id}
                className="transition-colors"
                style={{ borderTop: '1px solid var(--border)', background: editingId === u.id ? 'var(--bg-body)' : 'var(--bg-surface)' }}
              >
                <td className="px-3 py-2">
                  <div style={{ color: 'var(--text-primary)' }}>{u.display_name || u.name}</div>
                  {u.display_name && u.display_name !== u.name && (
                    <div style={{ color: 'var(--text-muted)' }}>@{u.name}</div>
                  )}
                </td>
                <td className="px-3 py-2" style={{ color: 'var(--text-secondary)' }}>{u.email || '—'}</td>
                <td className="px-3 py-2">
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{
                      background: u.is_persona ? 'rgba(139,92,246,0.15)' : 'rgba(59,130,246,0.15)',
                      color: u.is_persona ? '#a78bfa' : '#60a5fa',
                    }}
                  >
                    {u.is_persona ? 'Persona' : 'Person'}
                  </span>
                  {u.is_persona && (
                    <span
                      className="ml-1 px-1.5 py-0.5 rounded text-[10px]"
                      style={{
                        background: u.visibility === 'public' ? 'rgba(74,222,128,0.15)' : 'rgba(156,163,175,0.15)',
                        color: u.visibility === 'public' ? '#4ade80' : 'var(--text-muted)',
                      }}
                    >
                      {u.visibility === 'public' ? 'Public' : 'Private'}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {editingId === u.id ? (
                    <select
                      value={u.role}
                      onChange={e => updateUser(u.id, { role: e.target.value })}
                      className="px-2 py-1 rounded text-xs outline-none"
                      style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </select>
                  ) : (
                    <span className="capitalize" style={{ color: 'var(--text-secondary)' }}>{u.role}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                    style={{
                      background: u.status === 'active' ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)',
                      color: u.status === 'active' ? '#4ade80' : '#f87171',
                    }}
                  >
                    {u.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {/* Don't show actions for self */}
                    {u.id !== user?.userId && (
                      <>
                        {editingId === u.id ? (
                          <>
                            {u.is_persona ? (
                              <button
                                onClick={() => updateUser(u.id, { visibility: u.visibility === 'public' ? 'private' : 'public' })}
                                className="px-2 py-1 rounded text-[10px]"
                                style={{ color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                              >
                                {u.visibility === 'public' ? 'Make Private' : 'Make Public'}
                              </button>
                            ) : null}
                            <button
                              onClick={() => updateUser(u.id, { status: u.status === 'active' ? 'suspended' : 'active' })}
                              className="px-2 py-1 rounded text-[10px]"
                              style={{ color: u.status === 'active' ? 'var(--danger)' : 'var(--success, #4ade80)', border: '1px solid var(--border)' }}
                            >
                              {u.status === 'active' ? 'Suspend' : 'Activate'}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-2 py-1 rounded text-[10px]"
                              style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                            >
                              Done
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => setEditingId(u.id)}
                              className="px-2 py-1 rounded text-[10px]"
                              style={{ color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                            >
                              Edit
                            </button>
                            {u.email && !u.last_seen_at && (
                              <button
                                onClick={() => resendInvite(u.id)}
                                className="px-2 py-1 rounded text-[10px]"
                                style={{ color: 'var(--accent)', border: '1px solid var(--border)' }}
                              >
                                Resend Invite
                              </button>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {u.id === user?.userId && (
                      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>You</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═══ Main Settings Page ═══
export default function CommunitySettingsPage() {
  const { user, logout } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'owner'
  const [activeTab, setActiveTab] = useState<Tab>('profile')

  const tabs: { id: Tab; label: string; adminOnly?: boolean }[] = [
    { id: 'community', label: 'Community Settings', adminOnly: true },
    { id: 'profile', label: 'My Profile' },
    { id: 'users', label: 'Users', adminOnly: true },
  ]

  const visibleTabs = tabs.filter(t => !t.adminOnly || isAdmin)

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Settings</h1>
        <button
          onClick={logout}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{ color: 'var(--danger)', border: '1px solid var(--border)' }}
        >
          Sign Out
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-lg" style={{ background: 'var(--bg-body)' }}>
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex-1 text-xs font-medium py-2 px-3 rounded-md transition-colors"
            style={{
              background: activeTab === tab.id ? 'var(--bg-surface)' : 'transparent',
              color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: activeTab === tab.id ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        {activeTab === 'community' && isAdmin && <CommunitySettingsTab />}
        {activeTab === 'profile' && <MyProfileTab />}
        {activeTab === 'users' && isAdmin && <UsersTab />}
      </div>
    </div>
  )
}
